import { Controller, useFormContext } from "react-hook-form";

import { CustomInvokeCodeEditor } from "src/aiNode/CustomInvokeCodeEditor";
import { FieldErrors } from "src/api/types";
import { Card } from "src/base-components/Card";
import { AutocompleteCodeInput } from "src/base-components/CodeInput/EditorCodeInput";
import { AgentNodeData } from "src/constants/NodeDataTypes";
import { useIsFieldHighlighted } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";

type Props = {
  immutable: boolean;
  isReactive: boolean;
  nodeId: string;
  runFieldErrors?: FieldErrors;
};

export const AgentPromptEditor: React.FC<Props> = ({
  immutable,
  isReactive,
  runFieldErrors,
  nodeId,
}) => {
  const { control, watch } = useFormContext<AgentNodeData>();
  const filesId = watch("files.id") ?? "";
  const filesErrorMessage = runFieldErrors?.[filesId];
  const isSearchHighlighted = useIsFieldHighlighted(filesId);

  return (
    <div className="flex flex-col gap-y-2">
      <span className="font-inter-semibold-13px text-gray-800">
        Optional Agent input
      </span>
      <Card variant="default">
        <Card.Content>
          <div className="flex flex-col gap-y-5">
            <div className="flex flex-col gap-y-2">
              <span className="font-inter-semibold-13px text-gray-800">
                Additional instructions for the Agent
              </span>
              <span className="font-inter-normal-12px text-gray-500">
                Provide additional instructions to guide the agent
              </span>
              <Controller
                control={control}
                name="prompt"
                render={({ field }) => (
                  <div className="h-32">
                    <CustomInvokeCodeEditor
                      immutable={immutable}
                      isReactive={isReactive}
                      placeholder="e.g. You are a helpful assistant. Please analyze the following data and provide insights."
                      searchHighlightKey={`${nodeId}:prompt`}
                      value={field.value as string}
                      disableCompletion
                      onChange={field.onChange}
                    />
                  </div>
                )}
              />
            </div>

            <div className="flex flex-col gap-y-2" data-loc="agent-node-files">
              <span className="font-inter-semibold-13px text-gray-800">
                Include files
              </span>
              <span className="font-inter-normal-12px text-gray-500">
                Include a list of file IDs to be sent to the agent
              </span>
              <Controller
                control={control}
                name="files.expression"
                render={({ field }) => (
                  <AutocompleteCodeInput
                    dataLoc="agent-node-files-input"
                    disabled={immutable}
                    error={filesErrorMessage}
                    isSearchHighlighted={isSearchHighlighted}
                    placeholder="data.file_ids"
                    value={field.value ?? ""}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          </div>
        </Card.Content>
      </Card>
    </div>
  );
};
